import { useState } from 'react';
import Link from 'next/link';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import { classNames } from '@/utils/classNames';

import PropTypes from 'prop-types';

//libs
import { MEDIA_TYPES } from '@/lib/medias/mediaTypes';
import { POST, STORY } from '@/lib/posts/postTypes';
import { MEDIA } from '@/lib/routes/privateRoutes';
import { FACEBOOK, INSTAGRAM, LINKEDIN, X } from '@/lib/socialProfile';

import Button from '@/components/base/Button';
import Icon from '@/components/base/Icon';
//Components
import SimpleVideoPlayer from '@/components/base/SimpleVideoPlayer';
import Tooltip from '@/components/base/Tooltip';
import { Img } from '@/components/composite/MediaViewer/PostPreview/Img';
import { Trans } from '@/components/tools/translation';

const ItemWrapper = ({ network, ...restProps }) => {
    return (
        <div
            className={classNames(
                'media-item group relative flex w-full items-center justify-center overflow-hidden rounded-md bg-icoGray-100 dark:bg-icoDarkMode-deepBlack',
                {
                    square: [INSTAGRAM, FACEBOOK, X].includes(network),
                    'linkedin-preview-media': network === LINKEDIN
                }
            )}
            {...restProps}
        />
    );
};

export const Item = ({
    post,
    postType,
    mediaId,
    media,
    videoPlayedId,
    remoteMedia,
    setVideoPlayedId,
    config = { withAltText: true, imgFit: 'contain' },
    openInNewTab = true,
    repostEmail,
    ...restProps
}) => {
    const { t } = useTranslation('commons');
    const [isAltTextOpen, setIsAltTextOpen] = useState(false);
    const [repostState, setRepostState] = useState({
        submitSuccess: false,
        isSubmitting: false,
        error: null
    });

    // Feature not enabled for the moment
    const submitRepost = async () => {
        setRepostState({
            submitSuccess: false,
            isSubmitting: true,
            error: null
        });

        try {
            // Todo repost request
            setRepostState({
                isSubmitting: false,
                submitSuccess: true
            });

            setTimeout(() => {
                setRepostState((prevState) => ({
                    ...prevState,
                    submitSuccess: false
                }));
            }, 5000);
        } catch (e) {
            setRepostState({
                isSubmitting: false,
                error: t('errors:error_occured_submit_data')
            });
        }
    };

    return (
        <ItemWrapper {...restProps}>
            {media?.type === MEDIA_TYPES.TYPE_VIDEO || media?.type === MEDIA_TYPES.TYPE_REEL ? (
                <SimpleVideoPlayer
                    thumbnail={media?.thumbnail}
                    video={media?.video}
                    played={mediaId === videoPlayedId}
                    pauseCallback={() => {
                        setVideoPlayedId(null);
                    }}
                    playCallback={() => {
                        setVideoPlayedId(mediaId);
                    }}
                    className="flex h-full w-full items-center justify-center"
                />
            ) : (
                <Img
                    postId={post?.id}
                    mediaId={mediaId}
                    remoteMedia={remoteMedia}
                    src={media?.image ?? media?.thumbnail}
                    imgFit={config?.imgFit}
                />
            )}
            {postType !== STORY ? (
                isAltTextOpen ? (
                    <div className="absolute bottom-0 left-0 z-10 flex w-full items-end lg:p-m">
                        <div className="relative w-full rounded-b-lg bg-icoGray-300 p-m text-sm text-icoGray-500 lg:rounded-t-lg">
                            <p className="mb-2 text-xxs uppercase tracking-wider">
                                {t('media_viewer.alt_text')}
                            </p>
                            <p className="font-GMedium leading-snug">{media?.alt_text}</p>
                            <button
                                className="absolute right-1.5 top-1.5 h-5 w-5 rounded-full bg-icoGray-500 leading-none text-white"
                                onClick={() => {
                                    setIsAltTextOpen(false);
                                }}>
                                <Icon icon="close" className="align-bottom" />
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="absolute !top-auto bottom-0 left-0 z-10 hidden !h-auto w-full items-end justify-center space-x-3 p-2.5 group-hover:flex lg:p-m">
                        {media?.alt_text && config?.withAltText ? (
                            <Button
                                variant="light"
                                size="sm"
                                icon="visible"
                                className="px-2"
                                onClick={setIsAltTextOpen}>
                                {t('media_viewer.show_alt_text')}
                            </Button>
                        ) : null}
                        {post && repostEmail ? (
                            <Tooltip
                                position="top"
                                content={
                                    repostState?.error ? (
                                        repostState.error
                                    ) : (
                                        <Trans
                                            i18nKey={
                                                repostState.submitSuccess
                                                    ? 'commons:media_viewer.repost_tooltip_success'
                                                    : 'commons:media_viewer.repost_tooltip'
                                            }
                                            components={{ br: <br /> }}
                                            values={{ email: repostEmail }}
                                        />
                                    )
                                }>
                                <Button
                                    variant="light"
                                    size="sm"
                                    icon={!repostState?.error ? 'export' : null}
                                    isLoading={repostState?.isSubmitting}
                                    className="px-2"
                                    onClick={submitRepost}>
                                    {repostState?.error ? (
                                        <Icon icon="disconnect" className="mr-3 text-icoRed" />
                                    ) : null}
                                    {t('media_viewer.repost')}
                                </Button>
                            </Tooltip>
                        ) : null}
                        {post && openInNewTab ? (
                            <Link
                                target="_blank"
                                href={{
                                    pathname: MEDIA.replace('[id]', post.id),
                                    query: { social_profile_id: post?.social_profile_id }
                                }}>
                                <>
                                    <Button
                                        variant="light"
                                        size="sm"
                                        className="hidden px-2 lg:flex"
                                        target="_blank"
                                        rel="noopener noreferrer">
                                        {t('media_viewer.open_in_new_tab')}
                                    </Button>
                                </>
                            </Link>
                        ) : null}
                    </div>
                )
            ) : null}
        </ItemWrapper>
    );
};

Item.propTypes = {
    post: PropTypes.object,
    postType: PropTypes.oneOf([POST, STORY]),
    mediaId: PropTypes.number,
    media: PropTypes.object,
    videoPlayedId: PropTypes.number,
    remoteMedia: PropTypes.bool,
    setVideoPlayedId: PropTypes.func,
    config: PropTypes.object
};
